import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { chain, chunk, range } from 'lodash';

import { DropdownOption } from '@nstep-common/semantic-ui';
import { arrayHasChanged } from '@nstep-common/utils';
import {
	BaiscFilterSection,
	Filter,
	FilterChangeModel,
	FilterComparisonOperator,
	FilterType,
	LogService,
	PaginationModel,
	ReportBaseComponent,
	ReportColumn,
	ReportColumnSortDirection,
	SortDirection
} from '@nstep-common/core';

@Component({
	selector: 'app-report-view',
	templateUrl: './report-view.component.html',
	standalone: false
})
export class ReportViewComponent extends ReportBaseComponent implements OnChanges {

	@Input() isCollapsing = false;
	@Input() isCelled = false;

	@Input() isSortable = true;
	@Input() isFilterable = true;
	@Input() hideUnhideColumns = true;
	@Input() isExportable = true;
	@Input() isDraggable = true;

	@Input() columns: ReportColumn[] = [];
	@Input() data: any[][] | null = null;
	@Input() dataReady = true;

	@Input() pageSize = 10;
	@Input() currentPage = 1;
	@Input() totalPages = 1;

	@Output() pageSizeChangeEvent = new EventEmitter<number>();
	@Output() pageChangeEvent = new EventEmitter<PaginationModel>();
	@Output() filterChangeEvent = new EventEmitter<FilterChangeModel>();
	@Output() exportExcelEvent = new EventEmitter<boolean>();
	@Output() sortByColEvent = new EventEmitter<ReportColumnSortDirection>();

	filterDropdownSettings = {
		action: 'nothing'
	};

	dateCalendarSettings = {
		type: 'date',
		formatter: {
			date: 'DD/MM/YYYY'
		}
	}


	dateTimeCalendarSettings = {
		type: 'datetime',
		disableMinute: true,
		formatter: {
			datetime: 'DD/MM/YYYY h:mm A'
		}
	}

	shownItems: any[][] = [];
	sortData: any[][] = [];

	pages: number[] = [];
	maxPagesCount = 5;

	itemsPerPageDropdownValues: DropdownOption[] = [
		new DropdownOption({ value: 10, name: "10" }),
		new DropdownOption({ value: 25, name: "25" }),
		new DropdownOption({ value: 50, name: "50" }),
	];

	draggedColumnIndex!: number;

	constructor(protected logger: LogService) {
		super(logger);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (arrayHasChanged(changes['columns'])) {
			this.columns.filter(c => c.filterType !== this.FilterTypeEnum.Basic)
				.forEach(column => {
					column.allChecked = false;
				});

			this.columns.filter(c => c.filterType === this.FilterTypeEnum.Basic)
				.forEach(column => {
					column.assignedFilters[0].showInput = !this.filtersWithoutValue.includes(column.assignedFilters[0].operator);
					column.assignedFilters[1].showInput = !this.filtersWithoutValue.includes(column.assignedFilters[1].operator);
				});
		}

		if (arrayHasChanged(changes['data'])) {
			this.updateShownItems(true);
		}
	}

	sortByCol(col: ReportColumn): void {
		const sortation = new ReportColumnSortDirection({
			name: col.name,
			sortDirection: col.sortDirection === null ? SortDirection.ASC : col.sortDirection === SortDirection.ASC ? SortDirection.DESC : null,
		});

		this.sortByColEvent.emit(sortation);
	}

	dragStartColumn(index: number) {
		this.draggedColumnIndex = index;
	}

	stopEvent(event: Event) {
		event.preventDefault();
		event.stopPropagation();
	}

	dropColumn(index: number) {
		const cutOut = this.columns.splice(this.draggedColumnIndex, 1)[0];
		this.columns.splice(index, 0, cutOut);

		// Remake order number
		for (let i = 0; i < this.columns.length; i++) {
			this.columns[i].orderNumber = i + 1;
		}

		this.updateShownItems();
	}

	toggleAllColumns(): void {
		const state = !this.columns.length || this.columns[0].isShown;

		for (let col of this.columns) {
			col.isShown = !state;
		}

		this.updateShownItems();
	}

	updateShownItems(init = false): void {
		const shownColumnsKeys = chain(this.columns)
			.filter(c => c.isShown)
			.orderBy(c => c.orderNumber)
			.flatMap(c => c.key)
			.value();

		const items: any[][] = [];

		this.data?.forEach(row => {
			let newRow: any[] = [];

			shownColumnsKeys.forEach(key => {
				row.forEach(cell => {
					if (key === cell.key) {
						const newCell = { ...cell };
						newRow.push(newCell);
					}
				});
			});

			if (newRow.length) {
				items.push(newRow);
			}
		});

		this.shownItems = items;
		this.updatePages();
	}

	changePage(page: number) {
		if (page < 1 || page > this.totalPages) {
			return;
		}

		this.currentPage = page;

		this.pageChangeEvent.emit(new PaginationModel({
			pageSize: this.pageSize,
			currentPage: this.currentPage,
			totalPages: this.totalPages
		}));
	}

	exportExcel(isPartialDownload: boolean = false): void {
		this.exportExcelEvent.emit(isPartialDownload);
	}

	private updatePages(): void {
		if (!this.totalPages) {
			this.pages = [1];
			return;
		}

		const allPages = range(1, this.totalPages + 1);
		const pageInterval = chunk(allPages, this.maxPagesCount);

		this.pages = pageInterval.find(p => p.includes(this.currentPage))!;
	}

	setCurrentOperator(col: ReportColumn, operator: FilterComparisonOperator, filterSection: BaiscFilterSection): void {
		switch (filterSection) {
			case BaiscFilterSection.First:
				col.assignedFilters[0].operator = operator;
				col.assignedFilters[0].value = this.filtersWithoutValue.includes(operator) ? '-' : '';
				col.assignedFilters[0].showInput = !this.filtersWithoutValue.includes(operator);

				break;
			case BaiscFilterSection.Second:
				col.assignedFilters[1].operator = operator;
				col.assignedFilters[1].value = this.filtersWithoutValue.includes(operator) ? '-' : '';
				col.assignedFilters[1].showInput = !this.filtersWithoutValue.includes(operator);

				break;
		}
	}

	onCheckFilterValueChange(col: ReportColumn, filter: Filter): void {
		filter.value = filter.isChecked ? filter.text : '';

		col.allChecked = col.assignedFilters!.every(f => f.isChecked);
		col.selectedItemsCount = col.assignedFilters!.filter(f => f.isChecked).length;
	}

	applyColumnFilters(col: ReportColumn): void {
		if (col.filterType === FilterType.Basic && col.isDate) {
			col.assignedFilters?.forEach(filter => {
				filter.value = filter.dateValue && col.key === 'timestamp'
					? filter.dateValue.toISOString()
					: filter.dateValue
						? formatDate(filter.dateValue, 'yyyy-MM-dd', 'en')
						: '';
			});
		}

		this.filterChangeEvent.emit(new FilterChangeModel({ col: col }));
	}

	clearColumnFilters(col: ReportColumn): void {
		this.filterChangeEvent.emit(new FilterChangeModel({ col: col, clearColumnFilter: true }));

		col.allChecked = false;
		col.selectedItemsCount = col.assignedFilters!.filter(f => f.isChecked).length;
	}
}
